import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { analyticsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

export default function NpsPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!user) return;

    const timer = setTimeout(async () => {
      try {
        const res = await analyticsAPI.shouldPromptNps();
        if (res.data?.should_prompt) {
          setOpen(true);
        }
      } catch {
        // silently ignore
      }
    }, 10000); // Check 10s after login

    return () => clearTimeout(timer);
  }, [user]);

  const handleSubmit = async () => {
    if (!rating) return;
    try {
      await analyticsAPI.submitNps(rating, comment);
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
      setTimeout(() => setOpen(false), 1500);
    } catch {
      toast.error('Failed to submit feedback');
    }
  };

  const labels = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="nps-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg">How's your experience?</DialogTitle>
          <DialogDescription>
            Help us improve kaya.ai — rate your experience so far.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="py-6 text-center">
            <p className="text-zinc-600 text-sm">Thank you! Your feedback helps us improve.</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Star rating */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    data-testid={`nps-star-${star}`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? 'fill-amber-400 text-amber-400'
                          : 'text-zinc-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {(hoveredRating || rating) > 0 && (
                <p className="text-sm text-zinc-500">{labels[hoveredRating || rating]}</p>
              )}
            </div>

            {/* Optional comment */}
            <Textarea
              placeholder="Any feedback? (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="text-sm"
              data-testid="nps-comment"
            />

            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                Maybe later
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={!rating}
                data-testid="nps-submit"
                className="bg-[#D94E18] hover:bg-[#c44515]"
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
